import { ArrowLeftOutlined } from '@ant-design/icons'
import { useOrders } from '@api/useOrders'
import { OrderProducts } from '@components/_SiteWide/OrderDisplay/OrderProducts'
import { useTankRundown } from '@components/_SiteWide/TankDisplay/hooks/useTankRundown'
import { ProjectedDropToggle } from '@components/_SiteWide/TankDisplay/ProjectedDropToggle'
import { getInitialMarkerState } from '@components/_SiteWide/TankDisplay/TankGraphWidget'
import { VirtualList } from '@components/_SiteWide/VirtualList'
import { pageConfig } from '@components/OrderMovements/OrderMovements/MovementDetail/BOL/Detail/constants'
import { useSiteMonitoringContext } from '@components/StoreMonitoring/SiteMonitoringContext'
import { StoreDetailsTankRundown } from '@components/StoreMonitoring/StoreDetailsPage/StoreDetailsTankRundown'
import { onSiteArrowClick } from '@components/StoreMonitoring/Util/general'
import { orderStates } from '@constants/orderManagement'
import {
  addCommasToNumber,
  BBDTag,
  GraviButton,
  Horizontal,
  MaterialIcon,
  MobileControlBar,
  Texto,
  useDeviceType,
  useLocalStorage,
  Vertical,
} from '@gravitate-js/excalibrr'
import { ReadableDifference } from '@utils/general'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { Skeleton, Tabs, Tag } from 'antd'
import { addDays, subDays } from 'date-fns'
import moment from 'moment'
import { useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { MinNoFitColumn } from '../Grid/columnDefs'
import { TankRow } from './Dashboard'
import type { TankDetailProps } from './types'
import { getStorageKeys } from '@/pages/OrderManagement/All/util'
import { OrderFlagsCellRenderer } from '@/pages/OrderManagement/components/Grid/Columns/OrderColumnCellrenderers'
import { useSecurityContext } from '@/components/Navigation/SecurityContext'

export function Details({ store }: { store: Gravitate.Site }) {
  const { useOrdersQuery } = useOrders()
  const [searchParams] = useSearchParams()
  const orderNumber = useMemo(() => searchParams.get('order_number'), [searchParams])
  const { value: orderManagementDayRange } = useLocalStorage(getStorageKeys(true).dayRange, {
    back: -3,
    ahead: 1,
  })

  const { scopes } = useSecurityContext()
  const canViewOrders = scopes?.scopes?.includes('o.r') || scopes?.create_order

  const { data: ordersData, isFetching: isLoadingOrders } = useOrdersQuery(
    canViewOrders
      ? {
          ...pageConfig?.all?.filters,
          From: moment(
            subDays(new Date(), orderManagementDayRange?.back ? Math.abs(orderManagementDayRange.back) : 3)
          ).startOf('day'),
          To: moment(addDays(new Date(), orderManagementDayRange?.ahead ? orderManagementDayRange.ahead : 1)).endOf(
            'day'
          ),
          Stores: [store?.store_number],
          Order: orderNumber || undefined,
        }
      : undefined,
    true
  )

  const { value: rundownToggled, setValue: setToggle } = useLocalStorage('show_projected_drops', true)

  const { tankRundownData, setSelectedTank } = useTankRundown({
    store,
    shouldDefaultTankSelection: true,
  })

  return (
    <Vertical height='100%' style={{ overflow: 'hidden' }}>
      <SiteDetailsHeader store={store} rundownToggled={rundownToggled} />
      <div data-testid='details-container' style={{ backgroundColor: 'var(--bg-1)', flex: 1, overflow: 'auto' }}>
        <Vertical data-testid='details-tanks' className='p-3' gap='6px' height='auto' style={{ flexShrink: 0 }}>
          <Skeleton paragraph={{ rows: 12 }} loading={!store}>
            {store?.tanks?.map((tank) => (
              <div className='mobile-card p-3' key={tank.tank_id}>
                <TankRow tank={tank} rundownToggled={rundownToggled} />
              </div>
            ))}
          </Skeleton>
        </Vertical>
        <Vertical height='auto'>
          {!store && <Skeleton.Node active className='p-4' style={{ height: 300, width: '100%' }} />}
          {store && (
            <Tabs
              tabBarStyle={{ padding: '0 1.5em' }}
              onTabClick={(key) => setSelectedTank(store?.tanks.find((tank) => String(tank.tank_id) === key))}
              items={store?.tanks.map((tank) => ({
                label: tank.product,
                key: String(tank.tank_id),
                children: (
                  <TankDetail
                    store={store}
                    tank={tank}
                    tankRundownData={tankRundownData}
                    rundownToggled={rundownToggled}
                    setToggle={setToggle}
                  />
                ),
              }))}
            />
          )}
        </Vertical>
        {canViewOrders && (
          <Vertical height={ordersData?.rows?.length ? '100%' : 300}>
            <div className='px-4 pt-3 border-top border-bottom' style={{ backgroundColor: 'var(--bg-2)' }}>
              <MobileControlBar title='Site Orders' rowCount={ordersData?.rows?.length} />
            </div>
            <VirtualList
              data={ordersData?.rows ?? []}
              isLoading={isLoadingOrders}
              itemRenderer={(data) => <SiteOrderRow order={data} />}
            />
          </Vertical>
        )}
      </div>
    </Vertical>
  )
}

function SiteDetailsHeader({ store, rundownToggled }: { store: Gravitate.Site; rundownToggled: boolean | null }) {
  const navigate = useNavigate()
  const { isLandscape } = useDeviceType()
  const { detailStoreIds, filteredStores } = useSiteMonitoringContext()
  const index = detailStoreIds.findIndex((item: Gravitate.Site) => item?.id === store?.id)
  const { soonest_run_out, soonest_run_out_with_drops, retain } =
    filteredStores.find((item: Gravitate.Site) => store?.id === item?.id) || {}
  const disableBack = index === 0
  const disableForward = index === detailStoreIds.length - 1

  return (
    <Vertical data-testid='details-header' className='px-4 border-bottom' gap='2px' height='auto' flex='none'>
      <Skeleton loading={!store}>
        <Horizontal verticalCenter justifyContent='space-between'>
          <GraviButton
            theme2
            appearance='link'
            icon={<ArrowLeftOutlined />}
            style={{ padding: 0 }}
            onClick={() => navigate('/site_monitoring/dashboard')}
          >
            <Texto className='pl-1' category='h5'>
              Back to All Sites
            </Texto>
          </GraviButton>
          {!!detailStoreIds?.length && (
            <Horizontal gap='10px'>
              <GraviButton
                appearance='outlined'
                icon={<MaterialIcon icon='keyboard_arrow_left' size={18} />}
                disabled={disableBack}
                onClick={() => onSiteArrowClick(true, detailStoreIds, store.id, navigate)}
              />
              <GraviButton
                appearance='outlined'
                icon={<MaterialIcon icon='keyboard_arrow_right' size={18} />}
                disabled={disableForward}
                onClick={() => onSiteArrowClick(false, detailStoreIds, store.id, navigate)}
              />
            </Horizontal>
          )}
        </Horizontal>
        {!isLandscape && (
          <Texto category='h2' className='py-1'>
            {store?.name}
          </Texto>
        )}
        <Horizontal verticalCenter>
          <MaterialIcon icon='pin_drop' />
          <Texto className='pl-1'>{[store?.address, store?.city, store?.state].filter(Boolean).join(', ')}</Texto>
        </Horizontal>
        <Horizontal justifyContent='space-between'>
          <Horizontal verticalCenter>
            <MaterialIcon icon='storefront' />
            <Texto className='pl-1'>{store?.counterparty_name}</Texto>
          </Horizontal>
          <Horizontal verticalCenter>
            {store?.phone_number && (
              <>
                <MaterialIcon icon='call' />
                <Texto className='pl-1'>{store.phone_number}</Texto>
              </>
            )}
          </Horizontal>
        </Horizontal>
        <Horizontal className={isLandscape ? 'py-1' : 'pt-2 pb-3'} justifyContent='space-between'>
          <Tag className='m-0'>#{store?.store_number}</Tag>
          <Tag className='m-0'>
            <Horizontal verticalCenter gap='2px'>
              <Texto>Min Runout:</Texto>
              {ReadableDifference(
                rundownToggled ? soonest_run_out_with_drops : soonest_run_out,
                undefined,
                true,
                false,
                false
              )}
            </Horizontal>
          </Tag>
          <div style={{ minWidth: 50 }}>
            {retain && (
              <Tag className='m-0'>
                <Horizontal verticalCenter gap='2px'>
                  <Texto>Min No Fit:</Texto>
                  <MinNoFitColumn data={{ ...store, retain }} value={retain} />
                </Horizontal>
              </Tag>
            )}
          </div>
        </Horizontal>
      </Skeleton>
    </Vertical>
  )
}

function TankDetail({ store, tank, tankRundownData, rundownToggled, setToggle }: TankDetailProps) {
  const { value: graphMarkers } = useLocalStorage('tank_rundown_graph_markers', getInitialMarkerState())
  if (!store) return null
  return (
    <Vertical className='pt-4' height='auto'>
      <Horizontal className='px-4' justifyContent='space-between'>
        <Horizontal verticalCenter gap='4px'>
          <Texto category='h4' weight='bold'>
            {tank.product}
          </Texto>
          <Texto appearance='medium'>TANK {tank.tank_id}</Texto>
        </Horizontal>
        <ProjectedDropToggle
          isMobile
          value={!!rundownToggled}
          onChange={() => {
            setToggle(!rundownToggled)
          }}
        />
      </Horizontal>
      <Horizontal className='px-4 pt-2' gap='8px'>
        <Vertical className='mobile-card p-2'>
          <Texto appearance='medium'>Current Volume est.</Texto>
          <Texto weight='bold'>{addCommasToNumber(tank.current_volume)}</Texto>
        </Vertical>
        <Vertical className='mobile-card p-2'>
          <Texto appearance='medium'>Runout time</Texto>
          <Texto weight='bold'>{ReadableDifference(tank.run_out_date, undefined, true, false, false)}</Texto>
        </Vertical>
        <Vertical className='mobile-card p-2'>
          <Texto appearance='medium'>Current Ullage</Texto>
          <Texto weight='bold'>{addCommasToNumber(tank.storage_max - tank.current_volume, 0)}</Texto>
        </Vertical>
      </Horizontal>
      <Vertical className='px-2' height={300}>
        <StoreDetailsTankRundown
          timezone={store.timezone}
          tankRundownData={tankRundownData}
          rundownToggled={rundownToggled}
          graphMarkers={graphMarkers}
          isMobile
        />
      </Vertical>
    </Vertical>
  )
}

function SiteOrderRow({ order }: { order: Gravitate.Order }) {
  const updated = order?.updated_on ?? order?.created_date
  return (
    <Vertical className='p-3 px-4 border-bottom' style={{ backgroundColor: 'var(--bg-1)' }} gap='3px'>
      <Horizontal justifyContent='space-between' verticalCenter>
        <Horizontal verticalCenter>
          <Texto weight='bold' category='p2'>
            {order.loads.map((load) => load.location_name)}
          </Texto>
          <MaterialIcon icon='keyboard_arrow_right' size={18} />
          <Texto weight='bold' category='p2'>
            {order.drops.map((drop) => drop.location_name)}
          </Texto>
        </Horizontal>
        {order.state === orderStates.INPROGRESS && (
          <Texto appearance='medium' category='p2'>
            ETA: {formatters.SHORT_DATE_TIME(moment(order.estimated_end))}
          </Texto>
        )}
      </Horizontal>
      <Horizontal verticalCenter gap='1px'>
        <OrderProducts order={order} />
        <BBDTag theme1={order.state === orderStates.INPROGRESS} success={order.state === orderStates.COMPLETE}>
          {order.state}
        </BBDTag>
        <OrderFlagsCellRenderer data={order} />
      </Horizontal>
      <Texto appearance='medium' category='p2'>{`Last updated ${ReadableDifference(
        updated
      )} (${formatters.SHORT_DATE_TIME(new Date(updated))})`}</Texto>
    </Vertical>
  )
}
